import React, { useCallback, useState } from "react";
import "./ScholarLeague.scss";
import back_arrow from "../../../../utilities/images/engage/back_arrow.png";
import hugeicons_flash from "../../../../utilities/images/engage/hugeicons_flash.png";
import share_icon from "../../../../utilities/images/engage/share_icon.png";
import rank1_podium from "../../../../utilities/images/engage/rank1.png";
import rank2_podium from "../../../../utilities/images/engage/rank2.png";
import rank3_podium from "../../../../utilities/images/engage/rank3.png";
import lb_badge from "../../../../utilities/images/engage/lb_badge.png";
import { useHistory } from "react-router-dom";
import ShareMenu from "../ShareMenu/ShareMenu";

/** Order matches podium render: left = 2nd, center = 1st, right = 3rd */
const LEADERBOARD_PODIUM_RANK_IMAGES = [rank2_podium, rank1_podium, rank3_podium];

function leaderboardLeagueLine(name) {
  const label = String(name || "").trim();
  if (!label) return null;
  return (
    <small className="league-leaderboard__league-line">
      <img
        src={lb_badge}
        alt=""
        className="league-leaderboard__lb-badge"
        role="presentation"
      />
      {label}
    </small>
  );
}

/** XP for a daily/weekly block from getOverallGoalProgress (handles alternate API keys). */
function xpFromOverallPeriod(period) {
  if (period == null || typeof period !== "object") return null;
  const candidates = [
    period.xp_points,
    period["Points Earned Today"],
    period["XP Points"],
    period.today_xp,
    period.weekly_xp,
    period.total_xp,
    period.points_earned,
  ];
  for (const raw of candidates) {
    const n = Number(raw);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

const LeagueAchievements = ({
  leagueData,
  leaderboardData,
  leagueSteps,
  showHeader = true,
  compact = false,
  overallGoalProgress,
}) => {
  const history = useHistory();
  const [leaderboardTab, setLeaderboardTab] = useState("daily");

  const shareTitle =
    leagueData?.league_title?.trim() ||
    (typeof document !== "undefined" ? document.title : "") ||
    "My achievements";

  const handleBack = useCallback(() => {
    if (window.history.length > 1) {
      history.goBack();
      return;
    }
    history.push("/my-space");
  }, [history]);
  const handleCompactViewAchievements = useCallback(() => {
    history.push("/my-space/achievements");
  }, [history]);

  const tierFromXp = leagueSteps.find((step) => step.status === "current");
  const displayLeagueBadgeName =
    tierFromXp?.name || leagueData?.current_badge?.badge_name || "";
  const displayLeagueBadgeIcon =
    leagueData?.current_badge?.badge_icon_url || tierFromXp?.iconUrl || "";

  const overallDailyXp = xpFromOverallPeriod(overallGoalProgress?.daily);
  const overallWeeklyXp = xpFromOverallPeriod(overallGoalProgress?.weekly);

  const activeLeaderboard =
    leaderboardTab === "daily"
      ? leaderboardData?.daily_leaderboard
      : leaderboardData?.weekly_leaderboard;

  const topThree = [
    activeLeaderboard?.top_2 || null,
    activeLeaderboard?.top_1 || null,
    activeLeaderboard?.top_3 || null,
  ];

  const getInitials = (name) => {
    const clean = String(name || "").trim();
    if (!clean) return "U";
    const parts = clean.split(/\s+/).slice(0, 2);
    return parts.map((p) => p[0]?.toUpperCase() || "").join("") || "U";
  };

  const getXp = (row) =>
    Number(
      leaderboardTab === "daily" ? row?.today_xp ?? 0 : row?.weekly_xp ?? 0
    ) || 0;

  const leaderboardCurrentUserXp = (tab) => {
    const cu =
      tab === "daily"
        ? leaderboardData?.daily_leaderboard?.current_user
        : leaderboardData?.weekly_leaderboard?.current_user;
    const raw = tab === "daily" ? cu?.today_xp : cu?.weekly_xp;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  };

  /** League badge pill: today's XP on Today tab, weekly XP on This Week (fallback: leaderboard tiers). */
  const achievementsPillPoints = compact
    ? leaderboardTab === "daily"
      ? overallDailyXp != null
        ? overallDailyXp
        : leaderboardCurrentUserXp("daily")
      : overallWeeklyXp != null
        ? overallWeeklyXp
        : leaderboardCurrentUserXp("weekly")
    : overallWeeklyXp != null
      ? overallWeeklyXp
      : Number(leagueData?.weekly_xp ?? 0) || 0;

  const pointsPill = (
    <span className="points-pill">
      <img
        src={hugeicons_flash}
        alt="weekly_xp_icon"
        className="points-pill-icon"
      />
      <span>{achievementsPillPoints} pts</span>
    </span>
  );

  /** Current user row: prefer getOverallGoalProgress so totals match homepage / XP goal card. */
  const yourPositionPoints =
    leaderboardTab === "daily"
      ? overallDailyXp != null
        ? overallDailyXp
        : getXp(activeLeaderboard?.current_user)
      : overallWeeklyXp != null
        ? overallWeeklyXp
        : getXp(activeLeaderboard?.current_user);

  return (
    <div
      className={["league-achievements", "league-card", compact ? "league-card--compact" : ""]
        .filter(Boolean)
        .join(" ")}
    >
      <div className={`league-header ${showHeader ? "" : "league-header--no-title"}`.trim()}>
        {showHeader ? (
          <div className="league-title-wrap">
            <button
              type="button"
              className="league-back-button"
              onClick={handleBack}
              aria-label="Go back"
            >
              <img src={back_arrow} alt="" className="league-back-arrow" aria-hidden />
            </button>
            <h3>{leagueData?.league_title || ""}</h3>
          </div>
        ) : null}
        {!compact ? pointsPill : null}
      </div>

      <div className={`league-subtitle-wrap ${compact ? "league-subtitle-wrap--compact" : ""}`.trim()}>
        <div className="league-subtitle-main">
          <div className="league-badge-icon-wrap">
            <img
              src={displayLeagueBadgeIcon}
              alt=""
              className="league-badge-icon"
              aria-hidden
            />
          </div>
          <div className="league-subtitle-text">
            <p className="youre-in-text">You&apos;re in</p>
            <p className="league-name-text">
              <span>{displayLeagueBadgeName}</span> League
            </p>
          </div>
        </div>
        {compact ? pointsPill : null}
      </div>

      <div className="league-progress-scroll">
        <div className="league-progress">
          {leagueSteps.map((step) => (
            <div
              key={step.id ?? step.name}
              className={`league-step status-${step.status}`}
              style={
                step.status === "current" && step.ringAccent
                  ? { "--league-current-ring": step.ringAccent }
                  : undefined
              }
            >
              {step.status === "current" ? (
                <div className="league-youre-here-tooltip">
                  {step.hereLabel?.trim() ? step.hereLabel.trim() : "You're here"}
                </div>
              ) : null}
              <span className="step-dot">
                <img
                  src={step.iconUrl}
                  alt={`${step.name} icon`}
                  className="step-dot-icon"
                  loading="lazy"
                />
              </span>
              <p className="step-name">{step.name}</p>
              <p className="step-range">{step.range}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="league-progress-note">
        <img
          src={hugeicons_flash}
          alt=""
          className="league-progress-note-icon"
          aria-hidden
        />
        <span className="league-progress-note-text">
          {leagueData?.next_badge_message || ""}
        </span>
      </div>

      {compact ? (
        <div className="league-share-wrap">
          <div className="league-share-anchor">
            <button
              type="button"
              className="league-share-achievements-btn"
              onClick={handleCompactViewAchievements}
            >
              <span className="league-share-achievements-btn-label">
                View Achievements
              </span>
              <span className="league-share-achievements-btn-arrow" aria-hidden>
                &#8250;
              </span>
            </button>
          </div>
        </div>
      ) : (
        <ShareMenu
          shareLink={leagueData?.share_link}
          shareTitle={shareTitle}
          buttonLabel="Share your achievements"
          buttonIcon={share_icon}
          modalTitle="Share your achievements"
        />
      )}

      {compact && activeLeaderboard ? (
        <section className="league-leaderboard" aria-label="Leaderboard">
          <div className="league-leaderboard__header">
            <h4>Leaderboard</h4>
            <div className="league-leaderboard__tabs" role="tablist" aria-label="Leaderboard range">
              <button
                type="button"
                className={`league-leaderboard__tab ${
                  leaderboardTab === "daily" ? "is-active" : ""
                }`}
                onClick={() => setLeaderboardTab("daily")}
              >
                Today
              </button>
              <button
                type="button"
                className={`league-leaderboard__tab ${
                  leaderboardTab === "weekly" ? "is-active" : ""
                }`}
                onClick={() => setLeaderboardTab("weekly")}
              >
                This Week
              </button>
            </div>
          </div>

          <div className="league-leaderboard__podium">
            {topThree.map((row, idx) => (
              <div
                key={`${row?.user_id || idx}-top`}
                className={`league-leaderboard__top-card league-leaderboard__top-card--${
                  idx === 1 ? "first" : idx === 0 ? "second" : "third"
                }`}
              >
                <div className="league-leaderboard__top-card-upper">
                  <div className="league-leaderboard__avatar-stack">
                    <div className="league-leaderboard__avatar">
                      {row?.image ? (
                        <img src={row.image} alt={row?.name || "user"} />
                      ) : (
                        <span>{getInitials(row?.name)}</span>
                      )}
                    </div>
                    {String(row?.user_badge_tag || "").trim() ? (
                      <img
                        src={String(row.user_badge_tag).trim()}
                        alt={
                          row?.current_badge_name
                            ? `${row.current_badge_name} badge`
                            : "League badge"
                        }
                        className="league-leaderboard__badge-tag"
                        loading="lazy"
                      />
                    ) : null}
                  </div>
                  <p className="league-leaderboard__name">{row?.name || "-"}</p>
                  <small className="league-leaderboard__points">
                    {getXp(row)} pts
                  </small>
                </div>
                <div className="league-leaderboard__rank-podium">
                  <img
                    src={LEADERBOARD_PODIUM_RANK_IMAGES[idx]}
                    alt=""
                    className="league-leaderboard__rank-podium-img"
                    role="presentation"
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="league-leaderboard__list">
            {(activeLeaderboard?.others || []).map((row) => (
              <div className="league-leaderboard__row" key={`${row?.user_id}-row`}>
                <span className="league-leaderboard__row-rank">{row?.rank || "-"}</span>
                <div className="league-leaderboard__row-avatar">
                  {row?.image ? <img src={row.image} alt={row?.name || "user"} /> : <span>{getInitials(row?.name)}</span>}
                </div>
                <div className="league-leaderboard__row-main">
                  <p>{row?.name || "-"}</p>
                  {leaderboardLeagueLine(row?.current_badge_name)}
                </div>
                <strong>{getXp(row)} pts</strong>
              </div>
            ))}
          </div>

          {activeLeaderboard?.current_user ? (
            <>
              <p className="league-leaderboard__you-title">Your Position</p>
              <div className="league-leaderboard__row league-leaderboard__row--you">
                <span className="league-leaderboard__row-rank">
                  {leaderboardTab === "daily"
                    ? activeLeaderboard.current_user?.daily_rank ?? "-"
                    : activeLeaderboard.current_user?.weekly_rank ?? "-"}
                </span>
                <div className="league-leaderboard__row-avatar">
                  {activeLeaderboard.current_user?.image ? (
                    <img
                      src={activeLeaderboard.current_user.image}
                      alt={activeLeaderboard.current_user?.name || "user"}
                    />
                  ) : (
                    <span>{getInitials(activeLeaderboard.current_user?.name)}</span>
                  )}
                </div>
                <div className="league-leaderboard__row-main">
                  <p>{activeLeaderboard.current_user?.name || "-"}</p>
                  {leaderboardLeagueLine(activeLeaderboard.current_user?.current_badge_name)}
                </div>
                <strong>{yourPositionPoints} pts</strong>
              </div>
            </>
          ) : null}
        </section>
      ) : null}

    </div>
  );
};

export default LeagueAchievements;
